import { useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

type PwaNeedRefreshEvent = CustomEvent<{
  activate?: () => void;
}>;

const EVENT_NAME = 'ogs:pwa_need_refresh';
const SESSION_KEY = 'ogs_pwa_update_toast_shown_v1';

export function PwaUpdateToast() {
  const shownRef = useRef(false);

  useEffect(() => {
    const onNeedRefresh = (event: Event) => {
      if (shownRef.current) return;

      try {
        if (sessionStorage.getItem(SESSION_KEY) === '1') return;
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        // ignore
      }

      shownRef.current = true;

      const custom = event as PwaNeedRefreshEvent;
      const activate = custom?.detail?.activate;

      toast({
        title: 'Update available',
        description: 'A newer version is ready. Refresh to upgrade.',
        duration: 8000,
        action: (
          <ToastAction
            altText="Refresh to update"
            onClick={() => {
              try {
                activate?.();
              } catch {
                // ignore
              }
            }}
          >
            Refresh
          </ToastAction>
        ),
      });

      // Allow future updates in the same tab session, but avoid spam.
      // After a short window, permit another toast if a later update arrives.
      window.setTimeout(() => {
        shownRef.current = false;
      }, 60_000);
    };

    window.addEventListener(EVENT_NAME, onNeedRefresh as EventListener);
    return () => window.removeEventListener(EVENT_NAME, onNeedRefresh as EventListener);
  }, []);

  return null;
}
